// frontend/src/screens/ZodiacInfoScreen.jsx
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSignInfo } from '../data/zodiacInfo';
import SignInfoCard from '../components/SignInfoCard';
import { Helmet } from 'react-helmet-async';
import { localizedValue } from '../localization/presentationLanguage';
const signText = (es, en) => localizedValue({ es, en });

const ZINFO_BG_CSS = `
  /* Fondo idéntico a Welcome/Login (violeta + halo dorado + estrellas) */

  /* Desactivar pseudo-capas del edge-blur global para no mezclar tonos */
  #zinfoBgHost.edge-blur-bg::before,
  #zinfoBgHost.edge-blur-bg::after { content: none !important; }

  @keyframes slowSpinZinfo {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to   { transform: translate(-50%, -50%) rotate(360deg); }
  }

  /* Base violeta (mismo tono que Login) */
  #zinfoBgHost {
    width: 100%;
    min-height: 100vh;
    min-height: 100svh;
    min-height: 100dvh;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    background: radial-gradient(120% 100% at 50% 30%, rgba(255,215,0,0.12), rgba(75,0,130,0.18)),
                linear-gradient(180deg, #3b0a70 0%, #4a0f90 100%);
    background-color: #3b0a70;
    overscroll-behavior: none;
    overscroll-behavior-y: none;
  }

  /* Efecto estético: halo dorado + blur sutil (igual Login) */
  #zinfoBgHost::before {
    content: '';
    position: fixed;
    inset: 0;
    z-index: 1;
    background: radial-gradient(100vmax 100vmax at 52% 46%, rgba(218,165,32,0.12) 0%, rgba(218,165,32,0.06) 30%, rgba(106,13,173,0.05) 55%, transparent 70%);
    backdrop-filter: blur(3px) saturate(1.06) brightness(1.06);
    -webkit-backdrop-filter: blur(3px) saturate(1.06) brightness(1.06);
    mix-blend-mode: soft-light;
    pointer-events: none;
  }

  /* Estrellas (igual Login) */
  #zinfoBgHost::after {
    content: '';
    position: fixed;
    inset: 0;
    z-index: 0;
    background: radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.9), transparent 60%),
                radial-gradient(1px 1px at 80% 70%, rgba(255,255,255,0.7), transparent 60%),
                radial-gradient(1px 1px at 50% 10%, rgba(255,255,255,0.8), transparent 60%),
                radial-gradient(1px 1px at 10% 90%, rgba(255,255,255,0.6), transparent 60%);
    background-repeat: repeat;
    background-size: 300px 300px;
    animation: zinfoTwinkle 12s linear infinite;
    opacity: 0.6;
    pointer-events: none;
  }

  @keyframes zinfoTwinkle {
    0%, 100% { opacity: 0.6; transform: translateY(0); }
    50% { opacity: 0.9; transform: translateY(-2px); }
  }

  /* Vignette sutil (igual Login) */
  #zinfoBgHost .vignette {
    position: fixed;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: radial-gradient(120vmax 120vmax at 50% 45%, transparent 0 62%, rgba(0,0,0,0.10) 90%, rgba(0,0,0,0.18) 100%);
  }

  /* Ruleta giratoria, fija, detrás del contenido */
  #zinfoBgHost .spin-bg {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 90vmin;
    height: 90vmin;
    transform: translate(-50%, -50%);
    background-image: url("/logo/Zodiacus.jpeg");
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 50%;
    pointer-events: none;
    opacity: 1;
    filter: brightness(1.08) contrast(1.12) saturate(1.08);
    box-shadow: 0 0 60px rgba(0,0,0,0.18), inset 0 0 10px rgba(0,0,0,0.12);
    will-change: transform;
    transform-origin: center center;
    z-index: 0;
    animation: slowSpinZinfo 160s linear infinite;
  }

  @media (max-width: 600px) {
    #zinfoBgHost .spin-bg { width: 84vmin; height: 84vmin; opacity: .9; animation-duration: 120s; }
  }
  @media (max-width: 420px) {
    #zinfoBgHost .spin-bg { width: 80vmin; height: 80vmin; opacity: .88; }
  }
  @media (prefers-reduced-motion: reduce) {
    #zinfoBgHost .spin-bg { animation: none !important; transform: translate(-50%, -50%); }
  }
`;

const ZodiacInfoScreen = () => {
  const { sign } = useParams();
  const navigate = useNavigate();

  // SEO: canonical y metadatos
  const safeSign = (() => {
    try { return decodeURIComponent(sign || ''); }
    catch { return sign || ''; }
  })();
  const normalized = (safeSign || '').toString().trim().toLowerCase();
  const encoded = encodeURIComponent(normalized);
  const baseOrigin = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : 'https://zodiacus.app';
  const canonicalWhenFound = `${baseOrigin}/sign-info/${encoded}`;
  const canonicalIndex = `${baseOrigin}/sign-info`;

  const info = getSignInfo(safeSign);
  const displaySign = info?.nombre || info?.name || info?.title || (safeSign ? safeSign.charAt(0).toUpperCase() + safeSign.slice(1) : '');

  const handleBack = () => {
    const canGoBack = window.history?.state?.idx > 0;
    if (canGoBack) navigate(-1);
    else navigate('/sign-info');
  };

  if (!info) {
    return (
      <>
        <style>{ZINFO_BG_CSS}</style>
        <div
          id="zinfoBgHost"
          className="edge-blur-bg"
          style={{ '--bg-image-url': 'url("/logo/Zodiacus.jpeg")', position: 'relative', isolation: 'isolate' }}
        >
          <div className="vignette" aria-hidden="true" />
          <div className="spin-bg" aria-hidden="true" />
          <div style={styles.container}>
            <div style={styles.card}>
              <Helmet>
                <title>{signText('Zodiacus — Signo no encontrado', 'Zodiacus — Sign not found')}</title>
                <meta name="robots" content="noindex,follow" />
                <link rel="canonical" href={canonicalIndex} />
              </Helmet>
              <h2 style={styles.title}>{signText(`No encontramos información para “${safeSign || ''}”`, `We could not find information for “${safeSign || ''}”`)}</h2>
              <p style={styles.subtitle}>
                {signText('Verificá que el nombre del signo esté bien escrito o volvé al índice.', 'Check that the sign name is spelled correctly, or return to the index.')}
              </p>
              <div style={{ textAlign: 'center', marginTop: 18 }}>
                <button type="button" onClick={handleBack} style={styles.backBtn}>{signText('← Volver', '← Back')}</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{ZINFO_BG_CSS}</style>
      <div
        id="zinfoBgHost"
        className="edge-blur-bg"
        style={{ '--bg-image-url': 'url("/logo/Zodiacus.jpeg")', position: 'relative', isolation: 'isolate' }}
      >
        <div className="vignette" aria-hidden="true" />
        <div className="spin-bg" aria-hidden="true" />
        <div style={styles.container}>
          <div style={styles.card}>
            <Helmet>
              <title>{`Zodiacus — ${displaySign}`}</title>
              <meta
                name="description"
                content={signText(`Información completa sobre ${displaySign}: características, compatibilidades y más.`, `Complete information about ${displaySign}: traits, compatibility, and more.`)}
              />
              <link rel="canonical" href={canonicalWhenFound} />
            </Helmet>
            {/* Le pasamos el nombre del signo al componente */}
            <SignInfoCard sign={safeSign} />
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <button type="button" onClick={handleBack} style={styles.backBtn}>{signText('← Volver', '← Back')}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    minHeight: '100dvh',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    isolation: 'isolate',
    overflow: 'visible',
  },
  card: {
    width: '100%',
    maxWidth: 880,
    backgroundColor: 'rgba(255,255,255,0.88)',
    color: '#4B0082',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 20px 48px rgba(0,0,0,0.25)',
    border: '1px solid rgba(218,165,32,0.28)',
    backdropFilter: 'blur(6px)',
  },
  title: {
    margin: 0,
    textAlign: 'center',
    color: '#DAA520',
    fontWeight: 800,
    fontSize: 22,
    letterSpacing: 0.3,
  },
  subtitle: {
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 18,
    color: '#260046',
    fontWeight: 600,
  },
  backBtn: {
    backgroundColor: '#DAA520',
    color: '#4B0082',
    border: 'none',
    borderRadius: 10,
    padding: '10px 14px',
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  },
  emoji: {
    fontSize: 56,
    textAlign: 'center',
    marginBottom: 12,
    textShadow: '0 2px 6px rgba(0,0,0,0.5)',
  },
};

export default ZodiacInfoScreen;
